<template>
  <div class="SparkWeekPreview spark-card">
    <div class="week-head">
      <div class="week-title">
        {{ localize('sparks.week.title') }}
        <span v-if="areaLabel" class="week-title-area">· {{ areaLabel }}</span>
      </div>
      <div v-if="isEmpty" class="week-empty-note">{{ localize('sparks.week.empty.nothing_yet') }}</div>
    </div>

    <div v-if="isLoading && !preview" class="week-body" aria-hidden="true">
      <div v-for="n in 4" :key="n" class="week-row is-skeleton">
        <div class="row-time skeleton o-2" />
        <div class="row-main">
          <div class="row-title-skel skeleton o-1" />
          <div class="row-sub-skel skeleton o-3" />
        </div>
      </div>
    </div>

    <div v-else-if="!dayGroups.length" class="week-empty">
      <button type="button" class="week-empty-play" @click="emit('play')">
        <GamIcon :name="GamIconName.PLAY_EMPTY" size="var(--card-play-icon)" />
      </button>
      <div class="week-empty-title">{{ localize('sparks.week.empty.title') }}</div>
      <div class="week-empty-subtitle">{{ localize('sparks.week.empty.subtitle') }}</div>
    </div>

    <div v-else class="week-body">
      <section v-for="group in dayGroups" :key="group.date" class="week-day">
        <div class="week-day-head">
          <span class="week-day-label">{{ group.label }}</span>
          <span v-if="group.isDisabled" class="week-day-off">{{ localize('sparks.week.day_off') }}</span>
        </div>

        <button
          v-for="row in group.rows"
          :key="`${row.item.kind}-${row.item.id}`"
          type="button"
          :class="['week-row', `is-${row.item.kind}`, { 'is-mine': !!row.item.myStatus }]"
          @click="emit('select', row.item)"
        >
          <div class="row-time">{{ row.time }}</div>

          <div class="row-main">
            <div class="row-title">
              <GamIcon
                v-if="row.item.kind === 'gambit'"
                :name="GamIconName.PLAY_EMPTY"
                size="12px"
                :is-primary="row.item.isConfirmed"
                :is-current="!row.item.isConfirmed"
              />
              <GamIcon v-else :name="GamIconName.SPARK" size="12px" is-current />
              <span class="row-title-text">{{ row.title }}</span>
            </div>

            <div class="row-sub">
              <span :class="['row-kind', `is-${row.item.kind}`, { 'is-confirmed': row.item.isConfirmed }]">{{
                row.kindLabel
              }}</span>
              <template v-if="row.item.location">
                <span>·</span>
                <GamIcon :name="GamIconName.PIN" size="10px" is-current />
                <span class="row-location">{{ row.item.location.name }}</span>
              </template>
            </div>

            <div v-if="row.conflictLabel" class="row-warning">
              <GamIcon :name="GamIconName.WARNING" size="12px" is-current />
              <span>{{ localizeWithValues('sparks.conflict.attending', row.conflictLabel) }}</span>
            </div>
            <div v-else-if="row.item.isOutsideAvailability" class="row-warning is-soft">
              <GamIcon :name="GamIconName.TIME" size="12px" is-current />
              <span>{{ localize('sparks.week.outside_availability') }}</span>
            </div>
          </div>

          <div class="row-side">
            <div class="row-faces">
              <PlayerAvatar
                v-for="user in row.item.previewUsers"
                :key="user.id"
                :player="user"
                hide-rating
                is-overlapping
              />
              <span v-if="row.extraCount > 0" class="row-extra">+{{ row.extraCount }}</span>
            </div>
            <span v-if="row.item.myStatus" :class="['row-status', `is-${row.item.myStatus}`]">
              {{ localize(`sparks.week.status.${row.item.myStatus}`) }}
            </span>
          </div>
        </button>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SparkWeekPreview, SparkWeekPreviewItem } from '@/core/data/spark/spark.interface';
import { localize, localizeWithValues } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import PlayerAvatar from '@/views/main/sparks/components/PlayerAvatar.vue';
import {
  findAttendanceConflict,
  getAttendanceConflictLabel,
  type AttendanceConflictGambit,
} from '@/views/main/sparks/utils/attendance-conflict';
import { format, isToday, isTomorrow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { computed } from 'vue';

interface SparkWeekPreviewProps {
  preview: SparkWeekPreview | null;
  isLoading?: boolean;
  areaLabel?: string | null;
  attendedGambits?: AttendanceConflictGambit[];
}

interface WeekPreviewRow {
  item: SparkWeekPreviewItem;
  time: string;
  title: string;
  kindLabel: string;
  extraCount: number;
  conflictLabel: [string, string] | null;
}

interface WeekPreviewDayGroup {
  date: string;
  label: string;
  isDisabled: boolean;
  rows: WeekPreviewRow[];
}

const props = withDefaults(defineProps<SparkWeekPreviewProps>(), {
  isLoading: false,
  areaLabel: null,
  attendedGambits: () => [],
});

const emit = defineEmits<{
  select: [item: SparkWeekPreviewItem];
  play: [];
}>();

const parseDateKey = (dateKey: string): Date => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDayLabel = (dateKey: string): string => {
  const date = parseDateKey(dateKey);
  if (isToday(date)) return localize('sparks.week.today');
  if (isTomorrow(date)) return localize('sparks.week.tomorrow');

  return format(date, 'EEE, d LLL');
};

/* Unconfirmed gambits are challenges still waiting for the opponent. */
const getKindLabelKey = (item: SparkWeekPreviewItem): string => {
  if (item.kind === 'gambit') {
    if (item.isConfirmed) return 'sparks.week.kind.confirmed';
    return item.isEditedChallenge ? 'sparks.week.kind.edited_challenge' : 'sparks.week.kind.challenge';
  }

  return item.myStatus === 'invited' ? 'sparks.week.kind.invitation' : 'sparks.week.kind.proposal';
};

const buildRow = (item: SparkWeekPreviewItem): WeekPreviewRow => {
  const conflict = findAttendanceConflict(item, props.attendedGambits, [item.kind === 'gambit' ? item.id : null]);

  return {
    item,
    time: formatInTimeZone(item.start, item.timeZone, 'HH:mm'),
    title: item.title || item.location?.name || localize(`sparks.week.kind.${item.kind}`),
    kindLabel: localize(getKindLabelKey(item)),
    extraCount: Math.max(item.participantCount - item.previewUsers.length, 0),
    conflictLabel: conflict ? getAttendanceConflictLabel(conflict) : null,
  };
};

/* Empty days are skipped so a quiet week still fits on one card. */
const dayGroups = computed((): WeekPreviewDayGroup[] => {
  if (!props.preview) {
    return [];
  }

  return props.preview.days
    .map((day) => ({
      date: day.date,
      label: formatDayLabel(day.date),
      isDisabled: day.isDisabled,
      rows: props.preview!.items.filter((item) => item.date === day.date).map(buildRow),
    }))
    .filter((group) => group.rows.length > 0);
});

const isEmpty = computed(() => !(props.isLoading && !props.preview) && !dayGroups.value.length);
</script>

<style lang="scss" scoped>
.SparkWeekPreview {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1rem;
  border-radius: var(--radius-normal);
  background: var(--color-card-surface);
  overflow: hidden;
}

.week-head {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.week-empty-note {
  font-size: 14px;
  color: var(--color-white-50);
}

.week-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-white-100);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.week-title-area {
  font-size: 14px;
  font-weight: 400;
  color: var(--color-white-50);
}

.week-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.week-day {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.week-day-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-white-50);
}

.week-day-off {
  padding: 0 0.375rem;
  border-radius: var(--radius-small);
  background: var(--color-white-10);
  color: var(--color-white-40);
}

.week-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-normal);
  background: var(--color-white-5);
  color: inherit;
  text-align: left;
  cursor: pointer;

  &.is-mine {
    border-color: var(--color-pink);
  }

  &.is-skeleton {
    cursor: default;
  }
}

.row-time {
  min-width: 2.75rem;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--color-white-100);

  &.skeleton {
    height: 14px;
  }
}

.row-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.row-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-white-100);
}

.row-title-text,
.row-location,
.row-warning span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-title-skel {
  width: 60%;
  height: 14px;
}

.row-sub-skel {
  width: 40%;
  height: 10px;
}

.row-sub {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  font-size: 12px;
  color: var(--color-white-50);
}

.row-kind {
  flex-shrink: 0;

  &.is-proposal {
    color: var(--color-orange);
  }

  &.is-confirmed {
    color: var(--color-green);
  }
}

.row-warning {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  font-size: 11px;
  color: var(--color-orange);

  &.is-soft {
    color: var(--color-white-50);
  }
}

.row-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.row-faces {
  display: flex;
  align-items: center;

  :deep(.PlayerAvatar) {
    --card-avatar-size: 22px;
    width: 22px;
    height: 22px;
  }

  :deep(.PlayerAvatar + .PlayerAvatar) {
    margin-left: -6px;
  }

  :deep(.PlayerAvatar.is-overlapping .avatar) {
    box-shadow: 0 0 0 1px var(--color-white-60);
  }
}

.row-extra {
  margin-left: 0.25rem;
  font-size: 11px;
  color: var(--color-white-50);
}

.row-status {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-pink);
}

.week-empty {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  color: var(--color-white-50);
}

.week-empty-play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--card-play-size);
  height: var(--card-play-size);
  margin-bottom: 0.25rem;
  padding: 0;
  border: 2px solid var(--color-orange);
  border-radius: 50%;
  background: var(--color-orange);
  color: var(--color-dark-900);
  cursor: pointer;

  :deep(svg *) {
    fill: var(--color-dark-900) !important;
  }
}

.week-empty-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-white-100);
}

.week-empty-subtitle {
  max-width: 240px;
  font-size: 13px;
}
</style>
